import type { MakerAgent } from '@ralph/contracts'
import { ArrowLeft, BarChart3, Clock, Plus, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { OwnerLabel } from '@/components/marketplace/consumer/OwnerLabel.tsx'
import { InsightsPanel } from '@/components/marketplace/maker/InsightsPanel.tsx'
import { InvitesCard, pendingInvitesFor } from '@/components/marketplace/maker/InvitesCard.tsx'
import { privateSubjectPhraseFor } from '@/components/marketplace/maker/publish-wording.ts'
import { Badge, Button, EmptyState, Panel } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useMakerAgents } from '@/hooks/use-maker.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'

/**
 * "Your agents" — the maker's own shelf.
 *
 * Everything this person owns, maintains for a group they manage (PRD-26 D-19), or co-edits —
 * with the owner said wherever it is not simply them, so a group's agent reads as the group's.
 *
 * State, health and a sharing request waiting for review appear here and nowhere a consumer can
 * see them: a draft, a broken attachment and a queued publication are its maintainers' business,
 * and the gallery card deliberately carries none of them (AGM-001's card contract).
 *
 * `?insights=<agent>` opens that agent's activity and brings it into view: it is where the bell
 * sends a maintainer told that somebody shared a question.
 *
 * A shelf rather than a gallery, so the rows are wide instead of tiled: an owner comes here
 * to find the one agent that needs something done to it, and a state badge beside a name in
 * a single column is scannable in a way that six cards of equal weight are not.
 */
export const MakerAgentsPage = () => {
  // The back pill borrows the consumer gallery's own label, read in the body rather than
  // captured at import so it follows a locale switch (PRD-27 F-3, LOC-012).
  const back = useT().marketplace.gallery.about.back
  // The shelf's own words, read the same way. `const strings = makerStrings.mine` at module
  // scope is read once when the bundle evaluates, and every sentence here then stays in that
  // language whatever the reader switches to, with nothing failing (PRD-27 F-3).
  const messages = useT()
  const strings = messages.marketplace.maker.mine
  const session = useSession()
  const agents = useMakerAgents()
  const [searchParams] = useSearchParams()
  const focusInsights = searchParams.get('insights')

  const items = agents.data?.items ?? []
  const me = session.data?.user
  const invites = pendingInvitesFor(items, me?.id)
  const invitedIds = new Set(invites.map((agent) => agent.agentId))
  const mine = items.filter((agent) => !invitedIds.has(agent.agentId))

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto px-5 pb-10 sm:px-10">
      <div className="mx-auto w-full max-w-4xl">
        {/* The same back pill every other secondary agent surface carries. This page reached
            the gallery through nothing at all before — it was the one leaf in the agent tree
            with no way up. */}
        <Link
          to="/agents"
          className="readout mt-3.5 -mb-1 inline-flex h-[29px] items-center gap-[7px] rounded-full border border-rule bg-card pr-3 pl-[9px] text-[10px] tracking-[0.05em] text-fg-muted transition-[color,border-color,transform] duration-200 hover:-translate-y-px hover:border-rule-strong hover:text-fg"
        >
          <ArrowLeft size={12} strokeWidth={2.2} aria-hidden />
          {back}
        </Link>

        <div className="flex flex-wrap items-end gap-x-4 gap-y-3 pt-4 pb-5">
          <div className="min-w-0 flex-1 basis-[16rem]">
            <h1 className="display text-[30px] sm:text-[38px]">{strings.title}</h1>
            <p className="mt-[7px] max-w-[430px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">
              {strings.lead}
            </p>
          </div>
          <Link to="/agents/new" className="shrink-0">
            <Button variant="primary" icon={<Plus size={13} />}>
              {strings.create}
            </Button>
          </Link>
        </div>

        {invites.length > 0 && (
          <div className="mb-3">
            <InvitesCard invites={invites} />
          </div>
        )}

        {agents.isLoading && <p className="py-3 text-[13px] text-fg-subtle">{strings.loading}</p>}

        {agents.isError && <EmptyState title={strings.failed} body={describeApiError(messages, agents.error)} />}

        {!agents.isLoading && !agents.isError && mine.length === 0 && (
          <EmptyState
            title={strings.empty}
            body={strings.emptyBody}
            action={
              <Link to="/agents/new">
                <Button size="sm" icon={<Plus size={13} />}>
                  {strings.create}
                </Button>
              </Link>
            }
          />
        )}

        <ul className="space-y-2.5">
          {mine.map((agent, index) => (
            <li key={agent.agentId}>
              <MakerAgentRow
                agent={agent}
                viewerId={me?.id ?? null}
                index={index}
                openInsights={focusInsights === agent.agentId}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/**
 * One agent, with its insights folded away underneath (AGM-120).
 *
 * Collapsed by default and fetched only when opened: the shelf is a navigation surface, and
 * an owner with ten agents would otherwise wait on ten aggregate queries to read a list of
 * names. The panel id is derived from the agent id so `aria-controls` points at the panel
 * this button actually opens, even with several rows expanded at once.
 */
const MakerAgentRow = ({
  agent,
  viewerId,
  index,
  openInsights
}: {
  agent: MakerAgent
  viewerId: string | null
  index: number
  openInsights: boolean
}) => {
  const { formatAgo, formatList, t } = useI18n()
  const strings = t.marketplace.maker.mine
  const lifecycle = t.marketplace.publish.lifecycle
  const insights = t.marketplace.maker.insights
  const [insightsOpen, setInsightsOpen] = useState(openInsights)
  const rowId = `agent-row-${agent.agentId}`
  const panelId = `insights-${agent.agentId}`
  const coEditing = agent.viewerRole === 'editor' || agent.viewerRole === 'invited'
  // Said wherever the agent is not simply the reader's own: a group's, or somebody else's.
  const showOwner = agent.owner.kind === 'group' || agent.owner.id !== viewerId
  const waiting = agent.sharingRequest?.status === 'pending'

  useEffect(() => {
    if (!openInsights) return
    setInsightsOpen(true)
    document.getElementById(rowId)?.scrollIntoView({ block: 'center' })
  }, [openInsights, rowId])

  return (
    <Panel
      id={rowId}
      // Capped stagger: an owner at their agent limit would otherwise watch the shelf arrive
      // for two seconds, which stops being an entrance and becomes a wait.
      style={{ animationDelay: `${Math.min(index, 7) * 45}ms` }}
      className="rise px-4 py-3.5 transition-colors duration-200 hover:border-rule-strong"
    >
      {/* Wrapping rather than shrinking: below `sm` the two controls drop to their own line
          instead of squeezing the name into an ellipsis two characters long. */}
      <div className="flex flex-wrap items-start gap-3">
        <AgentAvatar icon={agent.icon} displayName={agent.displayName} accent={agent.accent} />

        <div className="min-w-0 flex-1 basis-[12rem]">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <h2 className="min-w-0 truncate font-sans text-[14.5px] font-semibold tracking-[-0.022em]">
              {agent.displayName}
            </h2>
            <Badge tone={agent.state === 'published' ? 'grounded' : agent.state === 'suspended' ? 'alert' : 'neutral'}>
              {lifecycle.state[agent.state]}
            </Badge>
            {coEditing && <Badge>{strings.coEditing}</Badge>}
            {waiting && (
              <span className="inline-flex items-center gap-1 text-[12px] text-fg-muted">
                <Clock size={11} aria-hidden />
                {strings.waitingForReview}
              </span>
            )}
            {agent.health.status === 'attention' && (
              <span className="inline-flex items-center gap-1 text-[12px] text-alert">
                <TriangleAlert size={11} aria-hidden />
                {strings.attention}
              </span>
            )}
          </div>
          {showOwner && (
            <p className="mt-1 flex min-w-0 items-center gap-1.5 text-[12px] text-fg-muted">
              <span className="eyebrow shrink-0">{t.marketplace.maker.owner.label}</span>
              <OwnerLabel owner={agent.owner} size={16} />
            </p>
          )}
          <p className="mt-1 truncate text-[12.5px] leading-[1.55] text-fg-muted">{agent.tagline}</p>
          {/* Who it is for, on the shelf where an owner asks exactly that. A row that said
              "Published" and nothing else left the difference between "the whole company has
              this" and "only I have this" one click away, on the list whose job is the
              overview (PRD-20 OWN-015). Only for a live one: a draft reaches nobody whatever
              its audience says, and the state badge beside this already says so. */}
          {agent.state === 'published' && (
            <p className="readout mt-1 truncate text-fg-subtle">
              {agent.audience.kind === 'everyone'
                ? strings.audienceEveryone
                : agent.audience.kind === 'private'
                  ? /* The mid-line entry, not the subject lower-cased (PRD-27 D-20). */
                    privateSubjectPhraseFor(t.marketplace.publish, agent.owner, viewerId)
                  : strings.audienceGroups(
                      { formatList },
                      agent.audience.groups.map((group) => group.name)
                    )}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            size="sm"
            icon={<BarChart3 size={12} />}
            aria-expanded={insightsOpen}
            aria-controls={panelId}
            onClick={() => setInsightsOpen((open) => !open)}
          >
            {insightsOpen ? insights.close : insights.open}
          </Button>
          <Link to={`/agents/${agent.agentId}/edit`}>
            <Button size="sm">{strings.open}</Button>
          </Link>
        </div>
      </div>

      <p className="readout mt-3 flex items-center gap-1.5 border-t border-rule pt-2.5 text-fg-subtle">
        <Clock size={11} aria-hidden />
        {strings.updated(formatAgo(agent.updatedAt))}
      </p>

      <div id={panelId}>
        <InsightsPanel agentId={agent.agentId} open={insightsOpen} />
      </div>
    </Panel>
  )
}
